import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Grid,
  Popper,
  Fade,
  Snackbar,
  Alert,
  InputAdornment,
  Link,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Calendar, Event as CalendarLibEvent } from 'react-big-calendar';
import { View } from 'react-big-calendar';
import momentLocalizer from 'react-big-calendar/lib/localizers/moment';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  EventNote as EventIcon,
  CardTravel as TripIcon,
  LocationOn as LocationIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import PhotoPopup from './photoPopup';
import { db } from '../../firebase/config';
// Import types but create local interface definitions to accommodate missing fields
import { Photo as PhotoType, Trip as TripType, Event as EventType } from '../../types/photos';

// Define more flexible types for the component
interface Photo extends Omit<PhotoType, 'userId' | 'location'> {
   userId?: string;
   location?: {
      name?: string;
      address?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
}

interface Trip extends Omit<TripType, 'userId' | 'createdAt' | 'location'> {
   userId?: string;
   createdAt?: Date;
   location?: {
      name?: string;
      address?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
}
interface Event extends Omit<EventType, 'userId' | 'createdAt' | 'location'> {
   userId?: string;
   createdAt?: Date;
   location?: {
      name?: string;
      address?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
}

// Set up the localizer for the Calendar
const localizer = momentLocalizer(moment);

// Custom styles for the Calendar
const calendarStyles = {
   calendar: {
      height: 650,
      padding: 2,
   },
   photoDay: {
      backgroundColor: 'rgba(255, 193, 7, 0.1)', // Amber for photos
      borderRadius: '4px',
      display: 'inline-block',
      width: '8px',
      height: '8px',
      margin: '2px',
   },
   selectedRange: {
      background:
         'linear-gradient(135deg, rgba(25, 118, 210, 0.3) 0%, rgba(25, 118, 210, 0.1) 100%)',
      color: '#000',
      fontWeight: 'bold',
      border: '2px solid rgba(25, 118, 210, 0.6)',
      borderRadius: '4px',
      boxShadow: '0 0 8px rgba(25, 118, 210, 0.2)',
   },
   photoCount: {
      position: 'absolute',
      bottom: 2,
      right: 2,
      fontSize: '10px',
      backgroundColor: 'rgba(25, 118, 210, 0.8)',
      color: 'white',
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
   },
   today: {
      backgroundColor: 'rgba(76, 175, 80, 0.15)',
   },
   photoThumbnails: {
      position: 'absolute',
      bottom: 2,
      right: 2,
      display: 'flex',
      gap: '2px',
   },
   photoThumbnail: {
      width: '14px',
      height: '14px',
      borderRadius: '2px',
      objectFit: 'cover',
      border: '1px solid white',
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
   },
   photoThumbnailMore: {
      width: '14px',
      height: '14px',
      borderRadius: '2px',
      backgroundColor: 'rgba(255, 193, 7, 0.8)', // Amber for photos
      fontSize: '10px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white',
      fontWeight: 'bold',
   },
   eventItem: {
      backgroundColor: 'rgba(25, 118, 210, 0.2)', // Blue for events
      color: '#1976d2',
      border: '1px solid rgba(25, 118, 210, 0.4)',
      borderLeft: '3px solid #1976d2',
   },
   tripItem: {
      backgroundColor: 'rgba(76, 175, 80, 0.2)', // Green for trips
      color: '#4caf50',
      border: '1px solid rgba(76, 175, 80, 0.4)',
      borderLeft: '3px solid #4caf50',
   },
   photoItem: {
      backgroundColor: 'rgba(255, 193, 7, 0.2)', // Amber for photos
      color: '#ff9800',
      border: '1px solid rgba(255, 193, 7, 0.4)',
      borderLeft: '3px solid #ff9800',
   },
};

// Props interface
interface PhotoCalendarViewProps {
   photos: Photo[];
   trips: Trip[];
   events: Event[];
   onCreateEvent: (
      startDate: Date,
      endDate: Date,
      selectedPhotoIds: string[],
      title: string,
      description: string
   ) => Promise<string>;
   onCreateTrip: (
      startDate: Date,
      endDate: Date,
      selectedPhotoIds: string[],
      title: string,
      description: string
   ) => Promise<string>;
   loading: boolean;
}

// Define a type for our calendar items - represents entries in the calendar view
interface CalendarItem {
   id: string;
   title: string;
   start: Date;
   end: Date;
   allDay: boolean;
   photoIds: string[];
   resourceType?: 'photo' | 'event' | 'trip';
   resource?: any;
}

// Photo preview component for hovering
interface PhotoPreviewProps {
   photo: Photo;
   onClose: () => void;
}

const PhotoPreview: React.FC<PhotoPreviewProps> = ({ photo, onClose }) => {
   return (
      <Card sx={{ maxWidth: 280, maxHeight: 350, overflow: 'auto' }}>
         <CardMedia
            component='img'
            height='160'
            image={photo.thumbnailUrl || photo.url}
            alt={photo.title}
         />
         <CardContent sx={{ p: 1 }}>
            <Typography variant='subtitle2' noWrap>
               {photo.title || 'Untitled Photo'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
               <CalendarIcon fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
               <Typography variant='caption' color='text.secondary'>
                  {moment(photo.takenAt).format('MMM DD, YYYY')}
               </Typography>
            </Box>
            {photo.location?.coordinates && (
               <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <LocationIcon fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant='caption' color='text.secondary' noWrap>
                     {photo.location.name || 'Location available'}
                  </Typography>
               </Box>
            )}
            {photo.tags?.length > 0 && (
               <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {photo.tags.slice(0, 3).map((tag, index) => (
                     <Chip key={index} label={tag} size='small' />
                  ))}
                  {photo.tags.length > 3 && (
                     <Chip label={`+${photo.tags.length - 3}`} size='small' variant='outlined' />
                  )}
               </Box>
            )}
         </CardContent>
      </Card>
   );
};

const PhotoCalendarView: React.FC<PhotoCalendarViewProps> = ({
   photos,
   trips,
   events,
   onCreateEvent,
   onCreateTrip,
   loading,
}) => {
   // Theme and responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   
   // State for selected date range
   const [selectedRange, setSelectedRange] = useState<{ start: Date; end: Date } | null>(null);

   // State for photos in selected range
   const [photosInRange, setPhotosInRange] = useState<Photo[]>([]);

   // State for photo preview
   const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
   const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLElement | null>(null);

   // State for day photo gallery modal
   const [dayGalleryOpen, setDayGalleryOpen] = useState(false);
   const [selectedDayPhotos, setSelectedDayPhotos] = useState<Photo[]>([]);
   const [selectedDay, setSelectedDay] = useState<string>('');

   // State for create dialog
   const [createDialogOpen, setCreateDialogOpen] = useState(false);
   const [createType, setCreateType] = useState<'event' | 'trip'>('event');
   const [createTitle, setCreateTitle] = useState('');
   const [createDescription, setCreateDescription] = useState('');

   // State for editable date range
   const [startDate, setStartDate] = useState<Date>(new Date());
   const [endDate, setEndDate] = useState<Date>(new Date());

   // State for toast notifications
   const [showToast, setShowToast] = useState(false);
   const [toastMessage, setToastMessage] = useState<React.ReactNode>('');
   const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'info'>('success');

   // State for processing status
   const [isProcessing, setIsProcessing] = useState(false);
   const [processingProgress, setProcessingProgress] = useState(0);

   // State for new item ID (for navigation)
   const [newItemId, setNewItemId] = useState<string>('');

   // State for calendar view
   const [calendarView, setCalendarView] = useState('month' as View);
   const [calendarDate, setCalendarDate] = useState(new Date());

   // Calendar events (photos, trips, events)
   const [calendarEvents, setCalendarEvents] = useState<CalendarItem[]>([]);

   // Organize photos by date
   const photosByDate = useCallback(() => {
      const photoMap = new Map<string, Photo[]>();

      photos.forEach((photo) => {
         const dateKey = moment(photo.takenAt).format('YYYY-MM-DD');
         if (!photoMap.has(dateKey)) {
            photoMap.set(dateKey, []);
         }
         photoMap.get(dateKey)?.push(photo);
      });

      return photoMap;
   }, [photos]);

   // Initialize calendar events
   useEffect(() => {
      // Reset calendar events
      const newCalendarEvents: CalendarItem[] = [];

      // Note: Removed photo count cards to reduce clutter
      // Photo thumbnails are still visible in day cells via DaySlotWrapper

      // Add trip events
      trips.forEach((trip) => {
         newCalendarEvents.push({
            id: `trip-${trip.id}`,
            title: trip.title,
            start: trip.startDate,
            end: trip.endDate,
            allDay: true,
            photoIds: photos.filter((p) => p.tripId === trip.id).map((p) => p.id),
            resourceType: 'trip',
            resource: trip,
         });
      });

      // Add regular events
      events.forEach((event) => {
         newCalendarEvents.push({
            id: `event-${event.id}`,
            title: event.title,
            start: event.startDate,
            end: event.endDate,
            allDay: true,
            photoIds: photos.filter((p) => p.eventId === event.id).map((p) => p.id),
            resourceType: 'event',
            resource: event,
         });
      });

      setCalendarEvents(newCalendarEvents);
   }, [photos, trips, events, photosByDate]);

   const handleEventClick = (event: CalendarLibEvent) => {
      console.log('Event clicked:', event);
   };
   // Handle date selection (range)
   const handleSelectSlot = (slotInfo: any) => {
      // Extract slot info data
      const { start, end, action } = slotInfo;

      // Create a range from start of first day to end of last day
      const rangeStart = moment(start).startOf('day').toDate();
      const rangeEnd = moment(end).endOf('day').toDate();

      if (action === 'select') {
         // Continue with normal range selection
         setSelectedRange({ start: rangeStart, end: rangeEnd });

         // Find photos in the selected range
         const photosInSelectedRange = photos.filter((photo) => {
            const photoDate = photo.takenAt;
            return photoDate >= rangeStart && photoDate <= rangeEnd;
         });

         setPhotosInRange(photosInSelectedRange);
      }

      if (action == 'click' || action == 'doubleClick') {
         const dateKey = moment(start).format('YYYY-MM-DD');
         const photosOnDay = photosByDate().get(dateKey) || [];

         if (photosOnDay.length > 0) {
            // Open the gallery modal
            setSelectedDayPhotos(photosOnDay);
            setSelectedDay(moment(start).format('MMMM D, YYYY'));
            setDayGalleryOpen(true);
            return; // Don't proceed with range selection
         }
      }
   };

   // Handle calendar event selection
   const handleSelectEvent = (event: CalendarLibEvent, e: React.SyntheticEvent) => {
      // Cast the event to our custom CalendarItem type to access our custom properties
      const calEvent = event as unknown as CalendarItem;

      // If it's a photo event, show preview of the first photo
      if (calEvent.resourceType === 'photo' && calEvent.photoIds.length > 0) {
         const photoId = calEvent.photoIds[0];
         const photo = photos.find((p) => p.id === photoId);
         if (photo) {
            setPreviewPhoto(photo);
            setPreviewAnchorEl(e.currentTarget as HTMLElement);

            // Close the popup after 5 seconds
            setTimeout(() => {
               setPreviewPhoto(null);
               setPreviewAnchorEl(null);
            }, 5000);
         }
      } else if (calEvent.photoIds && calEvent.photoIds.length > 0) {
         // For events or trips that have photos, show the first photo preview
         const photoId = calEvent.photoIds[0];
         const photo = photos.find((p) => p.id === photoId);
         if (photo) {
            setPreviewPhoto(photo);
            setPreviewAnchorEl(e.currentTarget as HTMLElement);

            // Close the popup after 5 seconds
            setTimeout(() => {
               setPreviewPhoto(null);
               setPreviewAnchorEl(null);
            }, 5000);
         }
      }
   };

   // Handle mouse enter on a day/cell
   const handleDayMouseEnter = (e: React.MouseEvent, date: Date) => {
      const dateKey = moment(date).format('YYYY-MM-DD');
      const photosOnDay = photosByDate().get(dateKey) || [];

      if (photosOnDay.length > 0) {
         setPreviewPhoto(photosOnDay[0]);
         setPreviewAnchorEl(e.currentTarget as HTMLElement);
      }
   };

   // Handle mouse leave
   const handleDayMouseLeave = () => {
      setPreviewPhoto(null);
      setPreviewAnchorEl(null);
   };

   // Handle day click to show photos
   const handleDayClick = (date: Date) => {
      const dateKey = moment(date).format('YYYY-MM-DD');
      const photosOnDay = photosByDate().get(dateKey) || [];

      if (photosOnDay.length > 0) {
         setSelectedDayPhotos(photosOnDay);
         setSelectedDay(moment(date).format('MMMM D, YYYY'));
         setDayGalleryOpen(true);
      }
   };

   // Create event/trip dialog with smart date handling
   const handleOpenCreateDialog = (type: 'event' | 'trip') => {
      if (!selectedRange) return;

      setCreateType(type);

      // Sort photos by date to identify the true date range
      const sortedPhotos = [...photosInRange].sort((a, b) => {
         return new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime();
      });

      // If there are photos, use their date range
      if (sortedPhotos.length > 0) {
         const firstPhotoDate = new Date(sortedPhotos[0].takenAt);
         const lastPhotoDate = new Date(sortedPhotos[sortedPhotos.length - 1].takenAt);

         setStartDate(firstPhotoDate);
         setEndDate(lastPhotoDate);

         // Create a meaningful title suggestion based on location data if available
         let locationName = '';
         const locations = sortedPhotos
            .filter((p) => p.location?.name)
            .map((p) => p.location?.name)
            .filter((value, index, self) => self.indexOf(value) === index);

         if (locations.length > 0) {
            locationName = locations[0] ?? '';
         }

         // Generate title suggestion
         const defaultTitle = locationName
            ? `${type === 'event' ? 'Event' : 'Trip'} to ${locationName}`
            : `${type === 'event' ? 'Event' : 'Trip'} on ${moment(firstPhotoDate).format('MMM D')}`;

         setCreateTitle(defaultTitle);
      } else {
         // Fall back to selected range
         setStartDate(selectedRange.start);
         setEndDate(selectedRange.end);

         const defaultTitle =
            type === 'event'
               ? `Event on ${moment(selectedRange.start).format('MMM D')}`
               : `Trip on ${moment(selectedRange.start).format('MMM D')}`;

         setCreateTitle(defaultTitle);
      }

      setCreateDialogOpen(true);
   };

   // Handle create event/trip submission with toast notification
   const handleCreateSubmit = async () => {
      if (!startDate || !endDate || photosInRange.length === 0) return;

      setIsProcessing(true);

      try {
         const photoIds = photosInRange.map((p) => p.id);

         let newId = '';

         if (createType === 'event') {
            // Simulate processing progress
            for (let i = 0; i <= 100; i += 20) {
               setProcessingProgress(i);
               if (i < 100) await new Promise((resolve) => setTimeout(resolve, 200));
            }

            // Pass title and description to event creation handler
            newId = await onCreateEvent(
               startDate,
               endDate,
               photoIds,
               createTitle,
               createDescription
            );
            setNewItemId(newId);

            setToastMessage(
               <span>
                  Event "{createTitle}" created successfully!{' '}
                  <Link
                     component='button'
                     onClick={() => (window.location.href = `/events/${newId}`)}
                     sx={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                     View now
                  </Link>
               </span>
            );
         } else {
            // Simulate processing progress
            for (let i = 0; i <= 100; i += 20) {
               setProcessingProgress(i);
               if (i < 100) await new Promise((resolve) => setTimeout(resolve, 200));
            }

            // Pass title and description to trip creation handler
            newId = await onCreateTrip(
               startDate,
               endDate,
               photoIds,
               createTitle,
               createDescription
            );
            setNewItemId(newId);

            setToastMessage(
               <span>
                  Trip "{createTitle}" created successfully!{' '}
                  <Link
                     component='button'
                     onClick={() => (window.location.href = `/trips/${newId}`)}
                     sx={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                     View now
                  </Link>
               </span>
            );
         }

         setToastSeverity('success');
         setShowToast(true);

         // Reset states
         setCreateDialogOpen(false);
         setCreateTitle('');
         setCreateDescription('');
         setSelectedRange(null);
         setPhotosInRange([]);
      } catch (error) {
         console.error(`Error creating ${createType}:`, error);
         setToastMessage(`Failed to create ${createType}. Please try again.`);
         setToastSeverity('error');
         setShowToast(true);
      } finally {
         setIsProcessing(false);
         setProcessingProgress(0);
      }
   };

   // Custom slot (day) component
   const DaySlotWrapper = ({ children, value }: { children: React.ReactNode; value: Date }) => {
      const dateKey = moment(value).format('YYYY-MM-DD');
      const photosOnDay = photosByDate().get(dateKey) || [];
      const isInSelectedRange =
         selectedRange && value >= selectedRange.start && value <= selectedRange.end;

      // Get first 3 photos for thumbnails
      const thumbnailPhotos = photosOnDay.slice(0, 3);
      const remainingCount = photosOnDay.length > 3 ? photosOnDay.length - 3 : 0;

      return (
         <div
            style={{
               position: 'relative',
               height: '100%',
               ...(isInSelectedRange ? calendarStyles.selectedRange : {}),
               cursor: photosOnDay.length > 0 ? 'pointer' : 'default',
            }}
            onMouseEnter={(e) => photosOnDay.length > 0 && handleDayMouseEnter(e, value)}
            onMouseLeave={handleDayMouseLeave}
         >
            {children}
            {photosOnDay.length > 0 && (
               <div style={calendarStyles.photoThumbnails as React.CSSProperties}>
                  {thumbnailPhotos.map((photo, index) => (
                     <img
                        key={`thumb-${photo.id}-${index}`}
                        src={photo.thumbnailUrl || photo.url}
                        alt=''
                        style={calendarStyles.photoThumbnail as React.CSSProperties}
                     />
                  ))}
                  {remainingCount > 0 && (
                     <div style={calendarStyles.photoThumbnailMore as React.CSSProperties}>
                        +{remainingCount}
                     </div>
                  )}
               </div>
            )}
         </div>
      );
   };

   // Render toolbar for date range actions
   const renderSelectedRangeToolbar = () => {
      if (!selectedRange || photosInRange.length === 0) return null;

      const startFormatted = moment(selectedRange.start).format('MMM D');
      const endFormatted = moment(selectedRange.end).format('MMM D, YYYY');
      const dateRangeText =
         startFormatted === endFormatted ? startFormatted : `${startFormatted} - ${endFormatted}`;

      return (
         <Paper
            sx={{
               p: isMobile ? 1.5 : 2,
               mb: isMobile ? 1.5 : 2,
               backgroundColor: 'primary.light',
               color: 'primary.contrastText',
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 2 : 0,
               }}
            >
               <Box>
                  <Typography variant={isMobile ? 'body1' : 'subtitle1'} sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                     {photosInRange.length} photos from {dateRangeText}
                  </Typography>
                  <Typography variant='body2' sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                     Create an event or trip with these photos
                  </Typography>
               </Box>
               <Box
                  sx={{
                     display: 'flex',
                     flexWrap: isMobile ? 'wrap' : 'nowrap',
                     gap: isMobile ? 0.75 : 1,
                     width: isMobile ? '100%' : 'auto',
                  }}
               >
                  <Button
                     variant='contained'
                     color='secondary'
                     startIcon={isMobile ? null : <EventIcon />}
                     onClick={() => handleOpenCreateDialog('event')}
                     size={isMobile ? 'small' : 'medium'}
                     sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', flex: isMobile ? '1' : '0 1 auto' }}
                  >
                     {isMobile ? 'Event' : 'Create Event'}
                  </Button>
                  <Button
                     variant='contained'
                     color='secondary'
                     startIcon={isMobile ? null : <TripIcon />}
                     onClick={() => handleOpenCreateDialog('trip')}
                     size={isMobile ? 'small' : 'medium'}
                     sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', flex: isMobile ? '1' : '0 1 auto' }}
                  >
                     {isMobile ? 'Trip' : 'Create Trip'}
                  </Button>
                  <IconButton
                     onClick={() => {
                        setSelectedRange(null);
                        setPhotosInRange([]);
                     }}
                     color='inherit'
                     size={isMobile ? 'small' : 'medium'}
                  >
                     <CancelIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }} />
                  </IconButton>
               </Box>
            </Box>
         </Paper>
      );
   };

   // Custom toolbar component
   const CustomToolbar = (toolbarProps: any) => {
      const { date, onNavigate, onView, label } = toolbarProps;

      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               mb: 2,
               px: isMobile ? 0 : 1,
               gap: 1.5,
            }}
         >
            {/* Navigation buttons - left aligned */}
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 0.5,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
               }}
            >
               <Button
                  onClick={() => onNavigate('PREV')}
                  startIcon={<ArrowBackIcon />}
                  size='small'
                  sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
               >
                  Previous
               </Button>
               <Button
                  onClick={() => onNavigate('TODAY')}
                  size='small'
                  sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
               >
                  Today
               </Button>
               <Button
                  onClick={() => onNavigate('NEXT')}
                  endIcon={<ArrowForwardIcon />}
                  size='small'
                  sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
               >
                  Next
               </Button>
            </Box>

            {/* Month/Year label and view buttons - left aligned */}
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 3,
                  justifyContent: 'flex-start',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
               }}
            >
               <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  sx={{
                     fontSize: isMobile ? '1rem' : '1.25rem',
                     fontWeight: 600,
                  }}
               >
                  {label}
               </Typography>

               <Box
                  sx={{
                     display: 'flex',
                     gap: 0.5,
                  }}
               >
                  <Button
                     onClick={() => onView('month')}
                     variant={calendarView === 'month' ? 'contained' : 'outlined'}
                     color='primary'
                     size='small'
                     sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', minWidth: isMobile ? 'auto' : 'auto' }}
                  >
                     Month
                  </Button>
                  <Button
                     onClick={() => onView('week')}
                     variant={calendarView === 'week' ? 'contained' : 'outlined'}
                     color='primary'
                     size='small'
                     sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', minWidth: isMobile ? 'auto' : 'auto' }}
                  >
                     Week
                  </Button>
                  <Button
                     onClick={() => onView('day')}
                     variant={calendarView === 'day' ? 'contained' : 'outlined'}
                     color='primary'
                     size='small'
                     sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', minWidth: isMobile ? 'auto' : 'auto' }}
                  >
                     Day
                  </Button>
               </Box>
            </Box>
         </Box>
      );
   };

    return (
      <Box
         sx={{
            mt: isMobile ? 1 : 2,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
         }}
      >
         {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
               <CircularProgress />
            </Box>
         ) : (
            <>
               {/* Selected range toolbar */}
               {renderSelectedRangeToolbar()}

               {/* Debug controls to ensure modal works */}
               <Box sx={{ mb: isMobile ? 1 : 2 }}>
                  {/* <Button
                     variant='outlined'
                     size='small'
                     onClick={() => {
                        // Find today's photos and open the modal
                        const today = new Date();
                        const dateKey = moment(today).format('YYYY-MM-DD');
                        const photosOnDay = photosByDate().get(dateKey) || [];

                        if (photosOnDay.length > 0) {
                           setSelectedDayPhotos(photosOnDay);
                           setSelectedDay(moment(today).format('MMMM D, YYYY'));
                           setDayGalleryOpen(true);
                        } else {
                           // If no photos today, find the first date with photos
                           const photoMap = photosByDate();
                           const firstDateWithPhotos = Array.from(photoMap.keys())[0];

                           if (firstDateWithPhotos) {
                              const firstDayPhotos = photoMap.get(firstDateWithPhotos) || [];
                              setSelectedDayPhotos(firstDayPhotos);
                              setSelectedDay(moment(firstDateWithPhotos).format('MMMM D, YYYY'));
                              setDayGalleryOpen(true);
                           }
                        }
                     }}
                  >
                     Show Sample Photos
                  </Button> */}
               </Box>

               {/* Calendar */}
               <Paper
                  sx={{
                     p: isMobile ? 1 : 2,
                     width: '100%',
                     maxWidth: '100%',
                     overflowX: 'auto',
                     boxSizing: 'border-box',
                  }}
               >
                  <Calendar
                     localizer={localizer}
                     events={calendarEvents}
                     startAccessor='start'
                     endAccessor='end'
                     style={{
                        ...calendarStyles.calendar,
                        minHeight: isMobile ? '400px' : '600px',
                     }}
                     selectable
                     onSelectSlot={handleSelectSlot}
                     //  onSelectEvent={(event) => {
                     //     // Handle the single event parameter to match expected signature
                     //     handleSelectEvent(event, {} as React.SyntheticEvent);
                     //  }}
                     onView={(view) => setCalendarView(view as any)}
                     onNavigate={(date) => setCalendarDate(date)}
                     view={calendarView}
                     date={calendarDate}
                     views={isMobile ? ['month', 'week', 'day'] : ['month', 'week', 'day', 'agenda']}
                     defaultView={isMobile ? 'month' : 'month'}
                     components={{
                        toolbar: CustomToolbar,
                     }}
                     eventPropGetter={(event) => {
                        // Cast to our CalendarItem type
                        const calEvent = event as unknown as CalendarItem;

                        // Apply styles based on resource type
                        if (calEvent.resourceType === 'event') {
                           return { style: calendarStyles.eventItem as React.CSSProperties };
                        } else if (calEvent.resourceType === 'trip') {
                           return { style: calendarStyles.tripItem as React.CSSProperties };
                        } else if (calEvent.resourceType === 'photo') {
                           return { style: calendarStyles.photoItem as React.CSSProperties };
                        }

                        return {};
                     }}
                     dayPropGetter={(date) => {
                        const dateKey = moment(date).format('YYYY-MM-DD');
                        const photosOnDay = photosByDate().get(dateKey) || [];
                        const isInSelectedRange =
                           selectedRange &&
                           date >= selectedRange.start &&
                           date <= selectedRange.end;

                        // Apply style conditionally based on photos and selection
                        return {
                           className: photosOnDay.length > 0 ? 'has-photos' : '',
                           style: {
                              position: 'relative',
                              ...(isInSelectedRange ? calendarStyles.selectedRange : {}),
                           },
                        };
                     }}
                     popup
                     tooltipAccessor={null}
                  />
               </Paper>

               {/* Photo preview popper */}
               <Popper
                  open={!!previewPhoto && !!previewAnchorEl}
                  anchorEl={previewAnchorEl}
                  placement='right-start'
                  transition
                  modifiers={[
                     {
                        name: 'offset',
                        options: {
                           offset: [0, 8],
                        },
                     },
                  ]}
               >
                  {({ TransitionProps }) => (
                     <Fade {...TransitionProps} timeout={350}>
                        <div>
                           {previewPhoto && (
                              <PhotoPreview
                                 photo={previewPhoto}
                                 onClose={() => {
                                    setPreviewPhoto(null);
                                    setPreviewAnchorEl(null);
                                 }}
                              />
                           )}
                        </div>
                     </Fade>
                  )}
               </Popper>

               {/* Enhanced Create Event/Trip Dialog */}
               <Dialog
                  open={createDialogOpen}
                  onClose={() => !isProcessing && setCreateDialogOpen(false)}
                  maxWidth='sm'
                  fullWidth
               >
                  <DialogTitle>
                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {createType === 'event' ? (
                           <EventIcon sx={{ mr: 1 }} />
                        ) : (
                           <TripIcon sx={{ mr: 1 }} />
                        )}
                        {createType === 'event' ? 'Quick Event Creation' : 'Quick Trip Creation'}
                     </Box>
                  </DialogTitle>
                  <DialogContent>
                     <Box sx={{ pt: 1 }}>
                        {/* A more prominent title field */}
                        <TextField
                           label='Title *'
                           placeholder={
                              createType === 'event' ? 'Name your event' : 'Name your trip'
                           }
                           fullWidth
                           value={createTitle}
                           onChange={(e) => setCreateTitle(e.target.value)}
                           margin='normal'
                           required
                           InputProps={{
                              startAdornment: (
                                 <InputAdornment position='start'>
                                    {createType === 'event' ? (
                                       <EventIcon color='primary' />
                                    ) : (
                                       <TripIcon color='primary' />
                                    )}
                                 </InputAdornment>
                              ),
                           }}
                        />

                        {/* Editable date fields */}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                           <Grid item xs={6}>
                              <TextField
                                 label='Start Date'
                                 type='date'
                                 fullWidth
                                 value={moment(startDate).format('YYYY-MM-DD')}
                                 onChange={(e) => setStartDate(new Date(e.target.value))}
                                 InputLabelProps={{ shrink: true }}
                              />
                           </Grid>
                           <Grid item xs={6}>
                              <TextField
                                 label='End Date'
                                 type='date'
                                 fullWidth
                                 value={moment(endDate).format('YYYY-MM-DD')}
                                 onChange={(e) => setEndDate(new Date(e.target.value))}
                                 InputLabelProps={{ shrink: true }}
                              />
                           </Grid>
                        </Grid>

                        {/* Optional quick description */}
                        <TextField
                           label='Brief Description (optional)'
                           placeholder='Add a short description - you can add more details later'
                           fullWidth
                           value={createDescription}
                           onChange={(e) => setCreateDescription(e.target.value)}
                           margin='normal'
                           multiline
                           rows={2}
                        />

                        {/* Photo selection with better visualization */}
                        <Box sx={{ mt: 2 }}>
                           <Typography
                              variant='subtitle2'
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              <ImageIcon fontSize='small' sx={{ mr: 1 }} />
                              Selected Photos ({photosInRange.length})
                           </Typography>

                           {/* Photo thumbnails with option to remove */}
                           <Grid
                              container
                              spacing={1}
                              sx={{ mt: 1, maxHeight: '120px', overflowY: 'auto' }}
                           >
                              {photosInRange.slice(0, 8).map((photo) => (
                                 <Grid item key={photo.id} xs={3}>
                                    <Box sx={{ position: 'relative' }}>
                                       <img
                                          src={photo.thumbnailUrl || photo.url}
                                          alt={photo.title || 'Photo'}
                                          style={{
                                             width: '100%',
                                             height: '50px',
                                             objectFit: 'cover',
                                             borderRadius: '4px',
                                          }}
                                       />
                                    </Box>
                                 </Grid>
                              ))}
                              {photosInRange.length > 8 && (
                                 <Grid item xs={3}>
                                    <Box
                                       sx={{
                                          height: '50px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: 'action.selected',
                                          borderRadius: '4px',
                                       }}
                                    >
                                       <Typography variant='caption'>
                                          +{photosInRange.length - 8} more
                                       </Typography>
                                    </Box>
                                 </Grid>
                              )}
                           </Grid>
                        </Box>

                        {/* Processing indicator */}
                        {isProcessing && (
                           <Box sx={{ width: '100%', mt: 2 }}>
                              <LinearProgress variant='determinate' value={processingProgress} />
                           </Box>
                        )}

                        {/* Quick completion message */}
                        <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                           <Typography
                              variant='caption'
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              <InfoIcon fontSize='small' sx={{ mr: 1 }} />
                              This is a quick create process. You can add more details after
                              creation.
                           </Typography>
                        </Box>
                     </Box>
                  </DialogContent>
                  <DialogActions>
                     <Button onClick={() => setCreateDialogOpen(false)} disabled={isProcessing}>
                        Cancel
                     </Button>
                     <Button
                        onClick={handleCreateSubmit}
                        variant='contained'
                        color='primary'
                        disabled={!createTitle.trim() || photosInRange.length === 0 || isProcessing}
                        startIcon={createType === 'event' ? <EventIcon /> : <TripIcon />}
                     >
                        {isProcessing
                           ? 'Creating...'
                           : `Create ${createType === 'event' ? 'Event' : 'Trip'}`}
                     </Button>
                  </DialogActions>
               </Dialog>

               {/* Toast Notification */}
               <Snackbar
                  open={showToast}
                  autoHideDuration={6000}
                  onClose={() => setShowToast(false)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
               >
                  <Alert
                     severity={toastSeverity}
                     variant='filled'
                     onClose={() => setShowToast(false)}
                     icon={toastSeverity === 'success' ? <CheckIcon /> : undefined}
                  >
                     {toastMessage}
                  </Alert>
               </Snackbar>

               {/* Photo Popup using the new component */}
               <PhotoPopup
                  open={dayGalleryOpen}
                  onClose={() => setDayGalleryOpen(false)}
                  photos={selectedDayPhotos}
                  selectedDay={selectedDay}
                  onCreateEvent={(photoIds) => {
                     // Filter selected photos
                     const selectedPhotos = selectedDayPhotos.filter((photo) =>
                        photoIds.includes(photo.id)
                     );

                     // Use selected photos to create an event
                     setPhotosInRange(selectedPhotos);
                     setSelectedRange({
                        start:
                           selectedPhotos.length > 0
                              ? moment(selectedPhotos[0].takenAt).startOf('day').toDate()
                              : new Date(),
                        end:
                           selectedPhotos.length > 0
                              ? moment(selectedPhotos[selectedPhotos.length - 1].takenAt)
                                   .endOf('day')
                                   .toDate()
                              : new Date(),
                     });

                     // Close the popup and open create event dialog
                     setDayGalleryOpen(false);
                     setCreateType('event');
                     setCreateDialogOpen(true);
                  }}
                  onCreateTrip={(photoIds) => {
                     // Filter selected photos
                     const selectedPhotos = selectedDayPhotos.filter((photo) =>
                        photoIds.includes(photo.id)
                     );

                     // Use selected photos to create a trip
                     setPhotosInRange(selectedPhotos);
                     setSelectedRange({
                        start:
                           selectedPhotos.length > 0
                              ? moment(selectedPhotos[0].takenAt).startOf('day').toDate()
                              : new Date(),
                        end:
                           selectedPhotos.length > 0
                              ? moment(selectedPhotos[selectedPhotos.length - 1].takenAt)
                                   .endOf('day')
                                   .toDate()
                              : new Date(),
                     });

                     // Close the popup and open create trip dialog
                     setDayGalleryOpen(false);
                     setCreateType('trip');
                     setCreateDialogOpen(true);
                  }}
               />
            </>
         )}
      </Box>
   );
};

export default PhotoCalendarView;
